'use client';

import { useTranslations } from 'next-intl';
import { useCountdown } from '@/hooks/useCountdown';
import { getPricingSpotlightState } from '@/config/site-campaigns';

const COUNTDOWN_LABELS = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'seconds', label: 'Seconds' },
];

const SEGMENT_TONE_CLASS = {
  'muted-small': 'relative -top-[0.08em] ml-1 align-middle font-sans text-[0.44em] font-medium text-neutral-400',
  gold: 'text-amber-300',
};

function normalizeRichLines(lines) {
  if (!Array.isArray(lines)) return null;
  return lines
    .map((line) => {
      if (typeof line === 'string') return [{ text: line }];
      if (Array.isArray(line)) {
        return line
          .map((segment) => {
            if (typeof segment === 'string') return { text: segment };
            if (segment && typeof segment === 'object') return segment;
            return null;
          })
          .filter((segment) => segment?.text);
      }
      if (line && typeof line === 'object' && line.text) return [line];
      return null;
    })
    .filter(Boolean);
}

function renderSegments(segments) {
  return segments.map((segment, index) => (
    <span
      key={`${segment.text}-${index}`}
      className={SEGMENT_TONE_CLASS[segment.tone] || undefined}
    >
      {segment.text}
    </span>
  ));
}

export default function PricingSpotlightCard({
  config,
  variant = 'page',
  id,
}) {
  const t = useTranslations('Campaigns.countdown');
  const spotlightState = getPricingSpotlightState(config);
  const countdown = useCountdown(config?.autoMode
    ? new Date(Date.parse(config.endAt) + 1).toISOString()
    : config?.endAt, {
    enabled: spotlightState.showCountdown,
  });

  if (!spotlightState.showCard) {
    return null;
  }

  const cardPadding = variant === 'modal'
    ? 'px-5 py-5 sm:px-6 sm:py-6'
    : 'px-5 py-6 sm:px-8 sm:py-7';
  const titleSize = variant === 'modal'
    ? 'text-[1.18rem] sm:text-[1.34rem] xl:text-[1.5rem]'
    : 'text-[1.2rem] sm:text-[1.42rem] xl:text-[1.58rem]';
  const rightPanelWidth = variant === 'modal'
    ? 'w-full lg:max-w-[340px]'
    : 'w-full lg:max-w-[360px]';
  const titleLineSegments = spotlightState.showCountdown
    ? (
      normalizeRichLines(config?.titleLineSegments)
      || normalizeRichLines(config?.titleLines)
      || normalizeRichLines([config?.title, config?.subtitle].filter(Boolean))
    )
    : (
      normalizeRichLines(config?.staticTitleLineSegments)
      || normalizeRichLines(config?.staticTitleLines)
      || normalizeRichLines([config?.staticTitle, config?.staticSubtitle].filter(Boolean))
    );
  const description = spotlightState.showCountdown ? config?.description : config?.staticDescription;
  const descriptionSegments = spotlightState.showCountdown
    ? normalizeRichLines(config?.descriptionSegments)?.[0]
    : normalizeRichLines(config?.staticDescriptionSegments)?.[0];
  const staticPanelBodyLines =
    normalizeRichLines(config?.staticPanelBodyLines)
    || (typeof config?.staticPanelBody === 'string'
      ? normalizeRichLines(config.staticPanelBody.split('\n').filter(Boolean))
      : null);
  const layoutClass = variant === 'modal'
    ? 'relative flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-center lg:gap-8'
    : 'relative flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-center lg:gap-10';

  return (
    <section
      id={id}
      className={`relative overflow-hidden rounded-[28px] border border-white/8 bg-[linear-gradient(135deg,rgba(34,31,28,0.98),rgba(54,43,34,0.98),rgba(31,28,25,0.98))] shadow-[0_36px_110px_rgba(0,0,0,0.42)] ${cardPadding}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(245,158,11,0.16),transparent_34%),radial-gradient(circle_at_top_right,rgba(251,191,36,0.11),transparent_28%)]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-[44%] bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.05),transparent_60%)]" />

      <div className={layoutClass}>
        <div className="lg:min-w-0 lg:pr-2">
          <span className="inline-flex items-center rounded-full bg-amber-400 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-black shadow-[0_12px_24px_rgba(245,158,11,0.22)]">
            {spotlightState.showCountdown ? config?.badgeLabel : config?.staticEyebrow}
          </span>

          <div className="mt-5 space-y-3">
            <div className="space-y-1.5">
              {titleLineSegments.map((lineSegments, index) => (
                <h2
                  key={index}
                  className={`font-playfair font-bold leading-[1.04] tracking-[-0.02em] text-white lg:whitespace-nowrap ${titleSize}`}
                >
                  {renderSegments(lineSegments)}
                </h2>
              ))}
            </div>

            {(descriptionSegments?.length || description) ? (
              <p className="text-[13px] leading-6 text-white/52 sm:text-[14px]">
                {descriptionSegments?.length ? renderSegments(descriptionSegments) : description}
              </p>
            ) : null}
          </div>
        </div>

        {spotlightState.showCountdown ? (
          <div className={`grid grid-cols-4 gap-2 rounded-[24px] border border-white/8 bg-white/[0.04] p-2.5 backdrop-blur-md sm:gap-2.5 sm:p-3 lg:justify-self-end ${rightPanelWidth}`}>
            {COUNTDOWN_LABELS.map((item, index) => {
              const isLast = index === COUNTDOWN_LABELS.length - 1;
              const value = countdown[item.key];

              return (
                <div key={item.key} className="relative flex flex-col items-center justify-center">
                  <div
                    suppressHydrationWarning
                    className={`flex h-[70px] w-full items-center justify-center rounded-[18px] text-[1.85rem] font-black tracking-tight sm:h-[76px] sm:text-[2rem] ${isLast
                    ? 'bg-amber-400 text-black shadow-[0_18px_32px_rgba(245,158,11,0.24)]'
                    : 'bg-white text-neutral-900'
                    }`}
                  >
                    {value}
                  </div>
                  <span className="mt-2 text-[10px] uppercase tracking-[0.12em] text-white/58">
                    {t(item.key)}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`rounded-[24px] border border-white/8 bg-white/[0.04] px-5 py-5 backdrop-blur-md lg:justify-self-end ${rightPanelWidth}`}>
            <span className="inline-flex items-center rounded-full bg-amber-400 px-3.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-black shadow-[0_12px_24px_rgba(245,158,11,0.18)]">
              {config?.staticPanelEyebrow || 'Ongoing Offer'}
            </span>
            <div className="mt-4 space-y-1 text-lg font-semibold text-white">
              {staticPanelBodyLines?.length ? (
                staticPanelBodyLines.map((lineSegments, index) => (
                  <p key={index}>{renderSegments(lineSegments)}</p>
                ))
              ) : (
                <p>Flash sale countdown ended, but the promoted price presentation remains active for conversion.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
